// Single shared HTTP client (FRONTEND_RULES §9). Slices' request functions import
// THIS — never axios directly, and never from a component. The request interceptor
// asserts identity (`X-User-Id`, sourced from localStorage — the dummy-login seam,
// persisted by auth.slice) + a correlation id (`X-Request-ID`); the response
// interceptor flattens the backend's RFC 9457 `problem+json` errors to a stable
// Error carrying `{ status, code, message }` so slices/thunks get one shape.
//
// Auth seam (INTEGRATION.md §4): identity is a dev placeholder — the participant
// chosen at the dummy login is persisted to localStorage and sent as `X-User-Id`.
// When SAML lands, only this injection (and the backend resolver) changes.
//
// CORS (LOCAL_SETUP §5): `X-User-Id`/`X-Request-ID` must be allow-listed on the
// backend or the browser preflight fails and blocks every call.

import axios from 'axios';
import { STORAGE_KEYS } from '../../config/constants';
import { getAccessTokenSync } from '../../auth/oidc/userManager';
import { getEmbeddedTokenSync } from '../../auth/embedded/embeddedToken';

// CRA bakes REACT_APP_* at build time — restart the dev server after changing it.
const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000/api/v3';

const requestId = () => {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {
    /* fall through */
  }
  return `req-${Date.now()}-${Math.round(Math.random() * 1e9).toString(16)}`;
};

export const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: false, // no cookies/Authorization this phase (LOCAL_SETUP §5)
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
});

// ── Request: inject the placeholder identity header + a correlation id ──
// Also attaches Accept-Language so the backend can localize errors/validation in the
// caller's language (the same code the footer selector persists to localStorage under
// STORAGE_KEYS.LANG). Accept-Language is a CORS-safelisted header, so no preflight
// allow-list change is needed. Fallback 'en' keeps the contract stable pre-i18n-init.
apiClient.interceptors.request.use((config) => {
  let userId = null;
  let lang = null;
  try {
    userId = localStorage.getItem(STORAGE_KEYS.USER_ID);
    lang = localStorage.getItem(STORAGE_KEYS.LANG);
  } catch {
    /* storage unavailable */
  }
  // Auth seam: attach a Bearer token when one exists — the IdP access token (OIDC
  // mode) or the dashboard session token (embedded mode) — otherwise fall back to the
  // dummy-login X-User-Id placeholder. Only this line-group changes per mode.
  const accessToken = getAccessTokenSync() || getEmbeddedTokenSync();
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  } else if (userId) {
    config.headers['X-User-Id'] = userId;
  }
  config.headers['Accept-Language'] = lang || 'en';
  config.headers['X-Request-ID'] = requestId();
  return config;
});

// ── Response: normalize RFC 9457 Problem Details into a single Error ──
// The backend returns `application/problem+json` ({ title, detail, status, type }).
// We surface one Error whose `message` is the most user-facing string and carry the
// structured fields (status, code, title, detail, problem) for callers that want them.
const normalizeError = (error) => {
  const res = error.response;
  if (!res) {
    const err = new Error(error.message || 'Network error — is the API running?');
    err.status = 0;
    err.code = 'NETWORK';
    return err;
  }
  const problem = res.data && typeof res.data === 'object' ? res.data : {};
  const message = problem.detail || problem.title || `HTTP ${res.status}`;
  const err = new Error(message);
  err.status = problem.status ?? res.status;
  // A backend-supplied `code` names the specific case; the RFC type URI is the fallback.
  err.code = problem.code || problem.type || String(res.status);
  err.title = problem.title;
  err.detail = problem.detail;
  err.problem = res.data;
  return err;
};

apiClient.interceptors.response.use(
  (response) => response,
  (error) => Promise.reject(normalizeError(error)),
);

/**
 * GET a path under the API base and return the parsed body.
 * @throws {Error} normalized error carrying { status, code, message }
 */
export async function apiGet(path) {
  const { data } = await apiClient.get(path);
  return data;
}

/**
 * POST a JSON body to a path under the API base and return the parsed body.
 * Used by the embedded-SSO handshake (/sso/accept); all other traffic is GET.
 * @throws {Error} normalized error carrying { status, code, message }
 */
export async function apiPost(path, body) {
  const { data } = await apiClient.post(path, body);
  return data;
}

export default apiClient;
